import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { mergeClasses, Text } from '@fluentui/react-components';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  readCompanyNames,
  readCompanyIndustries,
  readJobFunctions,
  readJobTitles,
  readJobSeniorities,
} from '@bingads-webui-campaign-react/linkedin-data';
import { SearchableMultiSelectorInput, useFilterOnClient } from '@bingads-webui-campaign-react/searchable-multi-selector-input';

import { LinkedInAudienceConfig, LinkedInAudienceType, LinkedInAudienceTypesInOrder } from '../consts';
import { CompanyNameOption } from './company-name-option';
import { useTargetingAudiencesStyles } from '../styles/targeting-audiences-styles';

const LinkedInAudienceCustomOptionRender = {
  [LinkedInAudienceType.COMPANY_NAMES]: CompanyNameOption,
};

const areTypesPresent = (typesToCheck, typesWithData) => _.any(typesToCheck, type => typesWithData.includes(type));

const mapCompanyNameResultsToViewModel = ({ value }) => value.map(({
  Id, Name, Industry, Size, LogoUrl,
}) => ({
  id: String(Id), name: Name, industry: Industry, size: Size, logo: LogoUrl,
}));

const mapResultsToViewModel = ({ value }) => value.map(({ Id, Name }) => ({ id: String(Id), name: Name }));

const useAudienceFetchFunctions = (cid, aid) => {
  const fetchCompanyNames = useCallback(query => readCompanyNames(cid, aid, query).then(mapCompanyNameResultsToViewModel), [cid, aid]);
  const fetchCompanyIndustries = useCallback(() => readCompanyIndustries(cid, aid).then(mapResultsToViewModel), [cid, aid]);
  const fetchJobFunctions = useCallback(() => readJobFunctions(cid, aid).then(mapResultsToViewModel), [cid, aid]);
  const fetchJobTitles = useCallback(query => readJobTitles(cid, aid, query).then(mapResultsToViewModel), [cid, aid]);
  const fetchJobSeniorities = useCallback(() => readJobSeniorities(cid, aid).then(mapResultsToViewModel), [cid, aid]);

  const cachedFetchCompanyIndustries = useFilterOnClient(fetchCompanyIndustries);
  const cachedFetchJobFunctions = useFilterOnClient(fetchJobFunctions);
  const cachedFetchJobSeniorities = useFilterOnClient(fetchJobSeniorities);

  return {
    [LinkedInAudienceType.COMPANY_NAMES]: fetchCompanyNames,
    [LinkedInAudienceType.COMPANY_INDUSTRIES]: cachedFetchCompanyIndustries,
    [LinkedInAudienceType.JOB_SENIORITIES]: cachedFetchJobSeniorities,
    [LinkedInAudienceType.JOB_FUNCTIONS]: cachedFetchJobFunctions,
    [LinkedInAudienceType.JOB_TITLES]: fetchJobTitles,
  };
};

export const TargetingAudiences = ({ data, onDataChange }) => {
  const i18n = useContext(I18nContext);
  const classes = useTargetingAudiencesStyles();

  const serverContext = useContext(ServerContext);
  const aid = useMemo(() => serverContext.CurrentAccount.Id, [serverContext]);
  const cid = useMemo(() => serverContext.CurrentCustomer.Id, [serverContext]);

  const fetchFunctions = useAudienceFetchFunctions(cid, aid);

  const typesWithData = useMemo(() =>
    _.filter(LinkedInAudienceTypesInOrder, type => !_.isEmpty(data[type])), [data]);

  const onAudienceDataChange = useCallback((type, value) =>
    onDataChange({ ...data, [type]: value }), [data, onDataChange]);

  return (
    <div className={mergeClasses('audiences-targeting', classes.root)}>
      <Text block>
        {i18n.getString(_TL_('How do you want to target your audience? Choose from the targeting options below.'))}
      </Text>
      {LinkedInAudienceTypesInOrder.map((type) => {
        const {
          label,
          helpTopic,
          helpLabel,
          placeholder,
          mutuallyExclusiveDisclaimer,
          mutuallyExclusiveTypes,
        } = LinkedInAudienceConfig[type];

        const disabled = areTypesPresent(mutuallyExclusiveTypes, typesWithData);

        return (
          <SearchableMultiSelectorInput
            className={`${type}-audience-targeting`}
            key={type}
            label={i18n.getString(label)}
            helpTopic={helpTopic}
            helpLabel={i18n.getString(helpLabel)}
            placeholderText={i18n.getString(placeholder)}
            disabled={disabled}
            tooltipText={disabled ? i18n.getString(mutuallyExclusiveDisclaimer) : undefined}
            fetchFn={fetchFunctions[type]}
            value={_.get(data, type, [])}
            onRenderOption={LinkedInAudienceCustomOptionRender[type]}
            onChange={_.partial(onAudienceDataChange, type)}
          />
        );
      })}
    </div>
  );
};

TargetingAudiences.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
